import React, { useEffect, useState } from 'react'
import { t } from 'ttag'
import { GoalWithProgress, UserId } from '../../services/api.types'
import YumButton from '../YumButton/YumButton'
import YumDrawer from '../YumDrawer/YumDrawer'
import YumGrid from '../YumGrid/YumGrid'
import YumManualEntryFormWrapper from '../YumManualEntryForm/YumManualEntryFormWrapper'
import makeStyles from '@material-ui/core/styles/makeStyles'
import NotificationsWrapper from '../Notification/NotificationsWrapper'
import { ReactComponent as PointsInactiveIcon } from '../../assets/activity-types/points-inactive.svg'
import { clearNotification } from '../../common/notifications/NotificationActions'
import { store } from '../../index'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import YumLogDetailsTableWrapper from './YumLogDetailsTableWrapper'

import { ReactComponent as DrawerCloseIcon } from '../../assets/v3/icons/ranking/drawer-close.svg'
import { isRtl } from '../../i18nInit'

export interface IYumLogActivityProps {
  goal: GoalWithProgress
  disabled: boolean
  autoClose: boolean
  isMeView: boolean
  isInProgress: boolean
  isBonus?: boolean
  userId?: UserId
  name?: string
  showManualEntryButton?: boolean
  /** Optional inline style for spacing or layout around the button */
  style?: React.CSSProperties
  /** When true, shows a smaller, more compact button with less padding */
  compact?: boolean
  challengeHasStarted?: boolean
}

const useStyles = makeStyles((theme) => ({
  button: {
    /*display: 'inline-block',
    padding: '0 10px',
    lineHeight: '32px',
    fontSize: '0.9375rem',
    fontWeight: 'bold',
    borderRadius: 8,
    width: 'auto',
    minWidth: 'auto',
    margin: 0,
    textTransform: 'none',*/
  },

  // Compact button style - smaller padding, more minimal appearance
  compactButton: {
    padding: '3px 8px',
    minWidth: 'auto',
    fontSize: '0.75rem',
    lineHeight: '16px',
    borderRadius: '6px',
    fontWeight: 400,
    height: '24px',
  },

  header: {
    marginBottom: '16px',
    borderBottom: '1px solid #E3E3E8',
    paddingBottom: '16px',
  },
  title: {
    fontSize: '1.3125rem',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  closeButton: {
    width: '1.5rem',
    height: '1.5rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 0,
    padding: 0,
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: '50%',
    '&:hover': {
      opacity: 0.8,
    },
  },

  bottomSafeArea: {
    /*transition: 'padding ease-in-out 150ms',
    paddingBottom: '16px',

    /!* iOS Safari 11.2, Safari 11 *!/
    '@supports (bottom: constant(safe-area-inset-bottom))': {
      paddingBottom: 'constant(safe-area-inset-bottom)',
    },

    /!* iOS Safari 11.4+, Safari 11.1+, Chrome 69+, Opera 56+ *!/
    '@supports (bottom: env(safe-area-inset-bottom))': {
      paddingBottom: 'env(safe-area-inset-bottom)',
    },*/
  },
}))

export default function YumLogActivity(props: IYumLogActivityProps) {
  const {
    goal,
    disabled,
    autoClose,
    isMeView,
    isInProgress,
    isBonus,
    userId,
    name,
    showManualEntryButton = false,
    style,
    compact = false,
    challengeHasStarted,
  } = props
  const classes = useStyles()

  let keyboardWillShowListener: PluginListenerHandle
  let keyboardWillHideListener: PluginListenerHandle
  useEffect(() => {
    //syncProgress()

    const addListeners = async () => {
      if (Capacitor.getPlatform() === 'ios') {
        keyboardWillShowListener = await Keyboard.addListener('keyboardWillShow', (info) => {
          const contentEl = document.getElementById('yum-drawer')?.getElementsByClassName('MuiPaper-root')[0]
          if (contentEl) {
            const safeAreaInsetBottom = parseInt(
              getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom'),
            )
            // @ts-ignore
            contentEl.style.transform = `translate(0px, -${info.keyboardHeight - safeAreaInsetBottom}px)`
            //contentEl.style.paddingBottom = `${info.keyboardHeight - parseInt(safeAreaInsetBottom)}px`
            //contentEl.style.transform = `translate(0px, -${info.keyboardHeight}px)`
            //contentEl.style.marginBottom = info.keyboardHeight + 'px'
          }
        })
        keyboardWillHideListener = await Keyboard.addListener('keyboardWillHide', () => {
          const contentEl = document.getElementById('yum-drawer')?.getElementsByClassName('MuiPaper-root')[0]
          if (contentEl) {
            // @ts-ignore
            contentEl.style.transform = `translate(0px, 0px)`
            //contentEl.style.paddingBottom = '0px'
            //contentEl.style.marginBottom = '0px'
          }
        })
      }
    }

    addListeners().catch(console.error)

    return () => {
      keyboardWillShowListener?.remove()
      keyboardWillHideListener?.remove()
    }
  }, [])

  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    // Clear any active notifications when closing the drawer
    store.dispatch(clearNotification())
  }

  const onSubmit = () => {
    if (autoClose) {
      setOpen(false)
    }
  }

  const title = !showManualEntryButton
    ? t`Diary`
    : isBonus
      ? t`Bonus points`
      : compact
        ? //? t`Diary + Entry`
          t`Diary`
        : t`Manual entry`

  // Check if button/link should be disabled
  const isDisabled = disabled || isInProgress

  return (
    <>
      {/* Compact mode: smaller button with less padding */}
      {compact ? (
        <YumButton
          size="small"
          variant={isBonus ? 'outlined' : 'contained'}
          className={classes.button}
          onClick={handleOpen}
          disabled={isDisabled}
          showLoader={isInProgress}
          immersiveLoader={true}
          // Keep immersive loader, but make it smaller for compact button.
          //className={classes.compactButton}
          //immersiveLoaderSize="small"
          style={style}
        >
          {showManualEntryButton ? (
            <svg
              style={
                isRtl
                  ? { marginLeft: '4px', width: '1rem', height: '1rem' }
                  : { marginRight: '4px', width: '1rem', height: '1rem' }
              }
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path d="M8 3L8 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 8L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : null}

          {title}
        </YumButton>
      ) : (
        <div style={style}>
          {/* Keep style handling consistent between compact and regular buttons */}
          <YumButton
            size="small"
            variant={isBonus ? 'outlined' : 'contained'}
            className={classes.button}
            onClick={handleOpen}
            disabled={isDisabled}
            showLoader={isInProgress}
            immersiveLoader={true}
          >
            {isBonus ? <PointsInactiveIcon style={{ width: 16, height: 16 }} /> : title}
          </YumButton>
        </div>
      )}
      {!disabled && (
        <YumDrawer
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-label': title }}
        >
          <YumGrid className={classes.header}>
            <div className={classes.title}>
              {title}
              {isBonus && name ? (
                <>
                  : <span style={{ fontWeight: 400 }}>{name}</span>
                </>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleClose}
              title={t`Close`}
              aria-label={t`Close`}
              className={classes.closeButton}
            >
              <DrawerCloseIcon aria-hidden="true" />
            </button>
          </YumGrid>
          <div className={classes.bottomSafeArea}>
            <NotificationsWrapper />
            {showManualEntryButton ? (
              <YumManualEntryFormWrapper
                goal={goal}
                onSubmit={onSubmit}
                isMeView={isMeView}
                isBonus={isBonus}
                userId={userId}
              />
            ) : null}
            {/* Show details table in compact mode (drawer context) */}
            {compact && challengeHasStarted ? (
              <div style={showManualEntryButton ? { marginTop: '16px' } : {}}>
                <YumLogDetailsTableWrapper goal={goal} />
              </div>
            ) : null}
          </div>
        </YumDrawer>
      )}
    </>
  )
}
